<template>
  <div class="messages">
    <ui-loader v-if="inLoading" />
    <div class="messages__heading">
      <category-selector
        :categories="categories"
        v-model="activeCategory"
        @update="fullUpdate"
      />
      <date-picker
        v-model="date"
        range
        :editable="false"
        input-class="form-control form-control-sm"
        placeholder="Date Range"
        @input="dateChanged"
      ></date-picker>
      <input name="search" class="search-bar" placeholder="Search" v-model="search" @keydown.enter="fullUpdate" />
      <template v-if="project && endpoint.id === 'all'">
        <button
          class="btn btn-sm btn-primary"
          @click="
            $root.$emit('create-form', { projectName: project.name, project })
          "
        >
          Create Form
        </button>
      </template>
      <template v-if="project && endpoint.id !== 'all'">
        <a
          class="btn btn-sm btn-primary"
          :href="`/projects/${endpoint.project_id}/forms/${endpoint.id}`"
          >Edit Form</a
        >
        <button
          class="btn btn-sm btn-danger"
          @click="$root.$emit('delete-form', { project, endpoint })"
        >
          Delete Form
        </button>
      </template>
      <button
        class="btn btn-sm btn-primary"
        @click="$root.$emit('export-submissions', { project, endpoint, date })"
      >
        Export
      </button>
    </div>
    <messages-list
      @set-active="setActiveMessage"
      @update="fullUpdate"
      :active-message-id="activeMessageId"
    >
      <message-body
        v-if="activeMessage"
        :active-message="activeMessage"
        @close="
          activeMessage = null
          activeMessageId = null
        "
        @close-update="fullUpdate"
      />
    </messages-list>

    <ui-pagination
      :max-pages="maxPages"
      v-model="currentPage"
      @input="updateMessages"
    />
  </div>
</template>

<script>
import API from '../../../api/'

import DatePicker from 'vue2-datepicker'

import UiTabs from '../ui-kit/ui-tabs.vue'
import UiPagination from '../ui-kit/ui-pagination.vue'
import UiLoader from '../ui-kit/ui-loader.vue'

import MessagesList from './messages-list.vue'
import MessageBody from './message-body.vue'
import CategorySelector from './controls/category-selector.vue'

export default {
  components: {
    DatePicker,
    UiTabs,
    UiLoader,
    UiPagination,
    CategorySelector,
    MessagesList,
    MessageBody
  },
  name: 'messages-root',
  data() {
    return {
      inLoading: false,
      date: null,
      activeCategory: 'inbox',
      limit: 12,
      currentPage: 1,
      activeMessage: null,
      activeMessageId: null,
      search: null,
    }
  },
  props: {
    endpoint: Object,
    project: Object
  },
  computed: {
    endpointId() {
      if (this.endpoint) {
        return this.endpoint.id
      } else {
        return null
      }
    },
    projectId() {
      if (this.project) {
        return this.project.id
      } else {
        return null
      }
    },
    maxPages() {
      const currentItemsCount =
        this.categories[this.activeCategory][0] || this.limit
      return Math.ceil(currentItemsCount / this.limit)
    },
    offset() {
      return (this.currentPage - 1) * this.limit
    },
    categories() {
      return this.$store.getters.categories
    }
  },
  methods: {
    async setActiveMessage(message) {
      this.activeMessageId = message.uuid
      try {
        this.activeMessage = {
          item: message,
          content: await API.messages.getBody(
            this.projectId,
            this.endpointId,
            message.uuid
          )
        }
        if (!message.is_read) {
          this.$store.commit('projectSubmissionRead', {
            projectId: this.project.id,
            endpointId: message.form_id
          })
          this.$store.commit('readMessage', {
            uuid: message.uuid
          })
          this.updateCount()
        }
      } catch (err) {
        this.activeMessage = null
        this.activeMessageId = null
        console.error(err)
      }
    },
    dateChanged() {
      this.currentPage = 1
      this.fullUpdate()
    },
    fullUpdate() {
      this.updateCount()
      this.updateMessages()
      this.activeMessage = null
      this.activeMessageId = null
    },
    async updateCount() {
      this.$store.dispatch('messagesCount', {
        endpointId: this.endpointId,
        projectId: this.projectId,
        date: this.date
      })
    },
    async updateMessages() {
      this.inLoading = true
      this.activeMessage = null
      this.activeMessageId = null
      try {
        await this.$store.dispatch('messages', {
          endpointId: this.endpointId,
          projectId: this.projectId,
          type: this.activeCategory,
          limit: this.limit,
          offset: this.offset,
          date: this.date,
          search: this.search
        })
      } catch (err) {
        console.error(err)
      } finally {
        this.inLoading = false
      }
    }
  },
  created() {
    this.$store.commit('setEndpoint', this.endpoint)
    this.updateCount()
    this.updateMessages()
  },
  watch: {
    endpoint() {
      this.$store.commit('setEndpoint', this.endpoint)
      this.updateCount()
      this.updateMessages()
      this.currentPage = 1
      this.activeMessage = null
      this.activeMessageId = null
      this.activeCategory = 'inbox'
    },
    activeCategory() {
      this.currentPage = 1
      this.updateMessages()
    }
  }
}
</script>

<style lang="scss">
.messages {
  position: relative;
  &__heading {
    display: flex;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid $gray-300;
    & > * {
      margin-right: 1rem;
    }
  }
  .pagination {
    padding-top: 1rem;
    border-top: 1px solid $gray-300;
  }
}
.search-bar {
    background-color: #f8f9fa;
    opacity: 1;
    height: calc(1.5em + 1rem + 2px);
    border: 1px solid #e9e9e9;
    border-radius: 0.2rem;
    line-height: 1.5;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
}
</style>
